import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import { useChatMutations, type ChatAtaDestino } from "@/shared/hooks/useChatOperacional";
import HelpTooltip from "@/shared/components/HelpTooltip";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/lib/utils";

type VinculoForm = "none" | "judicial" | "administrativo" | "extrajudicial" | "procedimento" | "atendimento_avulso";

interface OptPerfil {
  id: string;
  nome: string | null;
  qualificacao?: string | null;
}

interface OptVinculo {
  id: string;
  label: string;
}

const PERFIS_EXCLUIR = new Set(["cliente", "institution_external", "portal_client"]);

interface ChatNovaSalaModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSalaCriada?: (salaId: string) => void;
}

function escapeIlike(s: string) {
  return s.replace(/\\/g, "").replace(/%/g, "").replace(/_/g, "").trim().slice(0, 72);
}

async function buscarVinculos(tipo: VinculoForm, q: string): Promise<OptVinculo[]> {
  const term = escapeIlike(q);
  if (!term) return [];
  const pattern = `%${term}%`;

  try {
    if (tipo === "judicial") {
      const [rNum, rTit] = await Promise.all([
        supabase.from("processos").select("id, numero_processo, titulo").ilike("numero_processo", pattern).limit(10),
        supabase.from("processos").select("id, numero_processo, titulo").ilike("titulo", pattern).limit(10),
      ]);
      const map = new Map<string, { id: string; numero_processo: string | null; titulo: string | null }>();
      for (const r of [...(rNum.data ?? []), ...(rTit.data ?? [])]) map.set(r.id, r);
      return [...map.values()].slice(0, 15).map((r) => ({
        id: r.id,
        label: `${r.numero_processo || "—"} · ${(r.titulo || "").slice(0, 48)}`,
      }));
    }
    if (tipo === "administrativo") {
      const [rNum, rTit] = await Promise.all([
        supabase.from("processos_administrativos").select("id, numero, titulo").ilike("numero", pattern).limit(10),
        supabase.from("processos_administrativos").select("id, numero, titulo").ilike("titulo", pattern).limit(10),
      ]);
      const map = new Map<string, { id: string; numero: string | null; titulo: string | null }>();
      for (const r of [...(rNum.data ?? []), ...(rTit.data ?? [])]) map.set(r.id, r);
      return [...map.values()].slice(0, 15).map((r) => ({
        id: r.id,
        label: `${r.numero || "—"} · ${(r.titulo || "").slice(0, 48)}`,
      }));
    }
    if (tipo === "extrajudicial") {
      const [rNum, rTit] = await Promise.all([
        supabase.from("processos_extrajudiciais").select("id, numero, titulo").ilike("numero", pattern).limit(10),
        supabase.from("processos_extrajudiciais").select("id, numero, titulo").ilike("titulo", pattern).limit(10),
      ]);
      const map = new Map<string, { id: string; numero: string | null; titulo: string | null }>();
      for (const r of [...(rNum.data ?? []), ...(rTit.data ?? [])]) map.set(r.id, r);
      return [...map.values()].slice(0, 15).map((r) => ({
        id: r.id,
        label: `${r.numero || "—"} · ${(r.titulo || "").slice(0, 48)}`,
      }));
    }
    if (tipo === "procedimento") {
      const { data, error } = await supabase
        .from("procedimentos")
        .select("id, titulo, tipo")
        .ilike("titulo", pattern)
        .limit(15);
      if (error || !data?.length) return [];
      return data.map((r) => ({
        id: r.id,
        label: `${r.titulo || "—"}${r.tipo ? ` (${r.tipo})` : ""}`.slice(0, 72),
      }));
    }
    if (tipo === "atendimento_avulso") {
      const { data, error } = await supabase
        .from("atendimentos_avulsos")
        .select("id, titulo")
        .ilike("titulo", pattern)
        .limit(15);
      if (error || !data?.length) return [];
      return data.map((r) => ({ id: r.id, label: (r.titulo || "—").slice(0, 72) }));
    }
  } catch {
    return [];
  }
  return [];
}

export default function ChatNovaSalaModal({ open, onOpenChange, onSalaCriada }: ChatNovaSalaModalProps) {
  const { user } = useAuth();
  const { criarSala } = useChatMutations();
  const [titulo, setTitulo] = useState("");
  const [assunto, setAssunto] = useState("");
  const [vinculo, setVinculo] = useState<VinculoForm>("none");
  const [buscaVinc, setBuscaVinc] = useState("");
  const [optsVinc, setOptsVinc] = useState<OptVinculo[]>([]);
  const [vincSelecionado, setVincSelecionado] = useState<OptVinculo | null>(null);
  const [buscaPerfil, setBuscaPerfil] = useState("");
  const [meuProfileId, setMeuProfileId] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<Set<string>>(() => new Set());
  const [carregandoVinc, setCarregandoVinc] = useState(false);
  const [ataDestino, setAtaDestino] = useState<ChatAtaDestino>("nenhum");

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      const { data } = await supabase.from("profiles").select("id").eq("user_id", user.id).maybeSingle();
      setMeuProfileId(data?.id ?? null);
    })();
  }, [open, user]);

  const { data: perfisBrutos = [], isLoading: listaPerfisLoading } = useQuery({
    queryKey: ["chat-operacional-perfis-internos"],
    enabled: open && !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<OptPerfil[]> => {
      const { data, error } = await supabase
        .from("profiles_public")
        .select("id, nome, qualificacao")
        .order("nome", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter(
        (r) => r.id && (!r.qualificacao || !PERFIS_EXCLUIR.has(r.qualificacao)),
      ) as OptPerfil[];
    },
  });

  useEffect(() => {
    if (!open) {
      setTitulo("");
      setAssunto("");
      setVinculo("none");
      setBuscaVinc("");
      setOptsVinc([]);
      setVincSelecionado(null);
      setBuscaPerfil("");
      setSelecionados(new Set());
      setAtaDestino("nenhum");
    }
  }, [open]);

  const toggleParticipante = useCallback((id: string) => {
    setSelecionados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  useEffect(() => {
    if (!open || vinculo === "none") {
      setOptsVinc([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setCarregandoVinc(true);
      const rows = await buscarVinculos(vinculo, buscaVinc);
      setCarregandoVinc(false);
      setOptsVinc(rows);
    }, 320);
    return () => window.clearTimeout(t);
  }, [buscaVinc, vinculo, open]);

  useEffect(() => {
    setVincSelecionado(null);
    setBuscaVinc("");
    setOptsVinc([]);
  }, [vinculo]);

  const internosSemEu = useMemo(
    () => perfisBrutos.filter((p) => p.id && p.id !== meuProfileId),
    [perfisBrutos, meuProfileId],
  );

  const perfisFiltrados = useMemo(() => {
    const q = buscaPerfil.trim().toLowerCase();
    if (!q) return internosSemEu;
    return internosSemEu.filter((p) => (p.nome ?? "").toLowerCase().includes(q));
  }, [internosSemEu, buscaPerfil]);

  const temVinculoSelecionado = vinculo !== "none" && !!vincSelecionado;

  useEffect(() => {
    if (ataDestino === "vinculo" && !temVinculoSelecionado) {
      setAtaDestino("apenas_sala");
    }
  }, [ataDestino, temVinculoSelecionado]);

  const participantesOk = selecionados.size >= 1;
  const podeSalvar = titulo.trim().length > 0 && participantesOk && !criarSala.isPending;

  const payloadVinculo = useMemo(() => {
    if (vinculo === "none" || !vincSelecionado) {
      return {
        processo_id: null as string | null,
        processo_adm_id: null as string | null,
        processo_ext_id: null as string | null,
        procedimento_id: null as string | null,
        atendimento_avulso_id: null as string | null,
        client_id: null as string | null,
        institution_id: null as string | null,
      };
    }
    const id = vincSelecionado.id;
    return {
      processo_id: vinculo === "judicial" ? id : null,
      processo_adm_id: vinculo === "administrativo" ? id : null,
      processo_ext_id: vinculo === "extrajudicial" ? id : null,
      procedimento_id: vinculo === "procedimento" ? id : null,
      atendimento_avulso_id: vinculo === "atendimento_avulso" ? id : null,
      client_id: null,
      institution_id: null,
    };
  }, [vinculo, vincSelecionado]);

  const handleCriar = async () => {
    if (!podeSalvar) return;
    let destino: ChatAtaDestino = ataDestino;
    if (destino === "vinculo" && !temVinculoSelecionado) destino = "apenas_sala";
    try {
      const salaId = await criarSala.mutateAsync({
        titulo: titulo.trim(),
        assunto: assunto.trim() || undefined,
        participanteIds: [...selecionados],
        ata_destino: destino,
        ...payloadVinculo,
      });
      onSalaCriada?.(salaId);
      onOpenChange(false);
    } catch {
      /* toast no hook */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl w-[calc(100%-1.5rem)] bg-[#f9f6f0] border-[#a9abae]/30 flex flex-col max-h-[min(92dvh,880px)] overflow-hidden gap-0 p-0 sm:p-0 sm:max-h-[min(92dvh,900px)]">
        <div className="shrink-0 pl-4 sm:pl-6 pr-12 sm:pr-12 pt-4 sm:pt-5 pb-2">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#791614]">Nova sala de chat</DialogTitle>
            <DialogDescription className="sr-only">
              Crie uma nova sala de conversa entre advogados do escritório.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-4 px-4 sm:px-6 py-1 pr-3 [scrollbar-gutter:stable]">
          <div>
            <label className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">
              Título
            </label>
            <Input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Recurso TJ-00245 — estratégia"
              className="mt-1 border-[#a9abae]/30 bg-[#f9f6f0] text-foreground"
            />
          </div>

          <div>
            <label className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">
              Assunto (opcional)
            </label>
            <Textarea
              value={assunto}
              onChange={(e) => setAssunto(e.target.value)}
              rows={2}
              className="mt-1 border-[#a9abae]/30 bg-[#f9f6f0] resize-none text-foreground"
            />
          </div>

          <div>
            <div className="flex items-center gap-1">
              <label className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">
                Vínculo
              </label>
              <HelpTooltip text="Opcional. Vincula a conversa a um processo ou atendimento para facilitar o histórico e o registro da ata ao encerrar." />
            </div>
            <Select value={vinculo} onValueChange={(v) => setVinculo(v as VinculoForm)}>
              <SelectTrigger className="mt-1 border-[#a9abae]/30 bg-[#f9f6f0] text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem vínculo</SelectItem>
                <SelectItem value="judicial">Processo judicial</SelectItem>
                <SelectItem value="administrativo">Processo ADM</SelectItem>
                <SelectItem value="extrajudicial">Extrajudicial</SelectItem>
                <SelectItem value="procedimento">Procedimento</SelectItem>
                <SelectItem value="atendimento_avulso">Atendimento avulso</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {vinculo !== "none" && (
            <div>
              <label className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">
                Buscar registro
              </label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  value={buscaVinc}
                  onChange={(e) => setBuscaVinc(e.target.value)}
                  placeholder="Digite número ou título…"
                  className="pl-8 border-[#a9abae]/30 bg-[#f9f6f0] text-foreground"
                />
              </div>
              {carregandoVinc && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Buscando…
                </div>
              )}
              {!carregandoVinc && optsVinc.length > 0 && (
                <ScrollArea className="h-32 mt-2 rounded-md border border-[#a9abae]/30">
                  <div className="p-1 space-y-0.5">
                    {optsVinc.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setVincSelecionado(o)}
                        className={cn(
                          "w-full text-left text-xs px-2 py-1.5 rounded font-body text-foreground",
                          vincSelecionado?.id === o.id ? "bg-[#791614]/10 text-[#791614]" : "hover:bg-muted/40",
                        )}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {vincSelecionado && (
                <p className="text-[10px] text-muted-foreground mt-1 font-body">
                  Selecionado: {vincSelecionado.label}
                </p>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center gap-1">
              <label className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">
                Participantes do escritório
              </label>
              <HelpTooltip text="Marque um ou mais colegas (usuários internos). Você já entra na sala automaticamente. Clientes e portal externo não aparecem nesta lista." />
            </div>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={buscaPerfil}
                onChange={(e) => setBuscaPerfil(e.target.value)}
                placeholder="Filtrar por nome (opcional)…"
                className="pl-8 border-[#a9abae]/30 bg-[#f9f6f0] text-foreground"
              />
            </div>
            {listaPerfisLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Carregando equipe…
              </div>
            ) : (
              <ScrollArea className="h-48 mt-2 rounded-md border border-[#a9abae]/30">
                <div className="p-2 space-y-2">
                  {perfisFiltrados.map((p) => {
                    const ativo = selecionados.has(p.id);
                    return (
                      <label
                        key={p.id}
                        className={cn(
                          "flex items-center gap-3 rounded-md border px-2 py-2 cursor-pointer transition-colors",
                          ativo ? "border-[#791614]/40 bg-[#791614]/5" : "border-[#a9abae]/25 hover:bg-muted/30",
                        )}
                      >
                        <Checkbox
                          checked={ativo}
                          onCheckedChange={() => toggleParticipante(p.id)}
                          className="border-[#791614]/50 data-[state=checked]:bg-[#791614] data-[state=checked]:text-[#f9f6f0]"
                        />
                        <span className="text-sm font-body text-foreground flex-1 min-w-0 truncate">
                          {p.nome || "—"}
                        </span>
                      </label>
                    );
                  })}
                  {!listaPerfisLoading && perfisFiltrados.length === 0 && (
                    <p className="text-xs text-muted-foreground p-2">
                      Nenhum colega disponível ou filtro sem resultados.
                    </p>
                  )}
                </div>
              </ScrollArea>
            )}
            <p className="text-[10px] text-muted-foreground mt-1 font-body">
              Mínimo: 1 participante além de você ({selecionados.size} selecionado(s)).
            </p>
          </div>

          <div>
            <div className="flex items-center gap-1">
              <label className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">
                Ao encerrar a sala (IA)
              </label>
              <HelpTooltip text="Opcional. Ao encerrar, a IA lê o histórico e gera uma ata curta. Pode ficar só na sala ou também ser registrada no processo vinculado (quando houver vínculo)." />
            </div>
            <Select
              value={ataDestino}
              onValueChange={(v) => setAtaDestino(v as ChatAtaDestino)}
              disabled={false}
            >
              <SelectTrigger className="mt-1 border-[#a9abae]/30 bg-[#f9f6f0] text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nenhum">Não gerar ata</SelectItem>
                <SelectItem value="apenas_sala">Gerar ata e manter só na sala</SelectItem>
                <SelectItem value="vinculo" disabled={!temVinculoSelecionado}>
                  Gerar ata e registrar no vínculo (processo/procedimento)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="shrink-0 border-t border-[#a9abae]/25 bg-[#f9f6f0] px-4 sm:px-6 py-3 sm:py-4">
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-2 pt-0 sm:pt-0">
            <Button
              type="button"
              variant="outline"
              className="border-[#a9abae]/40 text-foreground w-full sm:w-auto"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto min-h-[40px] bg-[#791614] hover:bg-[#5a0f0e] text-[#f9f6f0] border-0 gap-2"
              disabled={!podeSalvar}
              onClick={() => void handleCriar()}
            >
              {criarSala.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  <span>Criando sala…</span>
                </>
              ) : (
                "Criar sala"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
